import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../services/ApiError';
import { translateBackendMessage } from '../../services/backendMessages';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  /** Zie LoginModal: in de native app is er niets om naar terug te keren. */
  dismissible?: boolean;
}

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ 
  isOpen, 
  onClose, 
  onSwitchToLogin ,
  dismissible = true
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const success = await requestPasswordReset(email);
      if (success) {
        setSuccess(true);
      } else {
        setError(t('forgotPassword.sendFailed'));
      }
    } catch (err) {
      // Een Apple-account krijgt hier geen resetmail maar een uitleg; die komt als ApiError.
      setError(err instanceof ApiError ? translateBackendMessage(t, err.detail) : t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-sluier bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block w-full align-bottom bg-white dark:ring-1 dark:ring-white/15 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="w-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-merk-tint-sterk flex items-center justify-center">
                        {success ? (
                          <CheckCircleIcon className="h-6 w-6 text-green-600" />
                        ) : (
                          <EnvelopeIcon className="h-6 w-6 text-hockey-brown" />
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-hockey-brown">
                        {t(success ? 'forgotPassword.sentTitle' : 'forgotPassword.title')}
                      </h3>
                      <p className="text-sm text-gray-500">
{t(success ? 'forgotPassword.sentSubtitle' : 'forgotPassword.subtitle')}
                      </p>
                    </div>
                  </div>
                  {dismissible && (
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loading}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  )}
                </div>

                {success ? (
                  /* Success State */
                  <div className="text-center">
                    <div className="mb-6">
                      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                        <CheckCircleIcon className="h-8 w-8 text-green-600" />
                      </div>
                      <p className="text-sm text-gray-700 mb-4">
                        {t('forgotPassword.sentTo')}
                      </p>
                      <p className="text-sm font-semibold text-hockey-brown bg-gray-50 px-3 py-2 rounded-md">
                        {email}
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">
                        {t('forgotPassword.nextStepsTitle')}
                      </h4>
                      <ul className="text-sm text-blue-700 space-y-1 text-left">
                        <li>• {t('forgotPassword.step1')}</li>
                        <li>• {t('forgotPassword.step2')}</li>
                        <li>• {t('forgotPassword.step3')}</li>
                        <li>• {t('forgotPassword.step4')}</li>
                      </ul>
                    </div>

                    <p className="text-xs text-gray-500 mb-6">
                      {t('forgotPassword.validity')}
                    </p>
                  </div>
                ) : (
                  /* Form State */
                  <form onSubmit={handleSubmit}>
                    {/* Error message */}
                    {error && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    )}

                    <div className="mb-6">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('fields.emailAddress')}
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-hockey-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder={t('fields.emailAddressPlaceholder')}
                        autoComplete="email"
                      />
                    </div>

                    {/* Deze notitie houdt bewust in het midden of het adres bekend is. Staat
                        er een foutmelding, dan is dat al beantwoord en spreekt hij die tegen. */}
                    {!error && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                        <h4 className="text-sm font-medium text-yellow-800 mb-1">
                          {t('forgotPassword.noticeTitle')}
                        </h4>
                        <p className="text-sm text-yellow-700">
                          {t('forgotPassword.notice')}
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || !email}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-hockey-blue text-base font-medium text-white hover:bg-hockey-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hockey-blue disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {t('forgotPassword.submitting')}
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          {t('forgotPassword.submit')}
                        </div>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {success ? (
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-hockey-blue text-base font-medium text-white hover:bg-hockey-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hockey-blue sm:ml-3 sm:w-auto sm:text-sm"
              >
                {t('forgotPassword.backToLogin')}
              </button>
            ) : (
              <button
                type="button"
                onClick={onSwitchToLogin}
                disabled={loading}
                className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hockey-blue sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('forgotPassword.backToLogin')}
              </button>
            )}
            
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hockey-blue sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common:actions.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};